import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, Share2, Loader2, Copy, ExternalLink, CheckCircle2, MessageSquareWarning } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ withCredentials: true });

export function ApprovalBadge({ approval }) {
  if (!approval) return null;
  const ok = approval.status === 'approved';
  return (
    <span data-testid="approval-badge" className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${ok ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
      {ok ? <CheckCircle2 size={11} /> : <MessageSquareWarning size={11} />} {ok ? 'Approved' : 'Changes requested'}
    </span>
  );
}

export default function HandoffShareBar({ vision, base, q, onChange }) {
  const [dl, setDl] = useState(false);
  const [sharing, setSharing] = useState(false);
  const shareUrl = vision.share_token ? `${window.location.origin}/vision/${vision.share_token}` : '';

  const download = async () => {
    setDl(true);
    try {
      const r = await ax.get(`${BACKEND}${base}/${vision.vision_id}/handoff${q}`, { responseType: 'blob' });
      const cd = r.headers['content-disposition'] || '';
      const name = (cd.match(/filename="([^"]+)"/) || [])[1] || 'website-vision-handoff.zip';
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Handoff package downloaded');
    } catch { toast.error('Could not build the package'); }
    finally { setDl(false); }
  };

  const share = async () => {
    setSharing(true);
    try {
      const r = await ax.post(`${BACKEND}${base}/${vision.vision_id}/share${q}`);
      onChange(r.data);
      const url = `${window.location.origin}/vision/${r.data.share_token}`;
      navigator.clipboard?.writeText(url);
      toast.success('Share link copied to clipboard');
    } catch { toast.error('Could not create share link'); }
    finally { setSharing(false); }
  };

  const ap = vision.approval;
  return (
    <div className="bg-gradient-to-br from-[#0A0F1C] to-[#13213f] text-white rounded-xl p-4 mt-6" data-testid="handoff-share-bar">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div>
          <div className="font-bold text-sm">Review & hand off</div>
          <div className="text-xs text-slate-300">Share for approval, then download everything your builder needs in one ZIP.</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={share} disabled={sharing} data-testid="vision-share-btn"
            className="inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-full bg-white/10 border border-white/20 hover:bg-white/20 disabled:opacity-60 transition-colors">
            {sharing ? <Loader2 size={15} className="animate-spin" /> : <Share2 size={15} />} {shareUrl ? 'Copy share link' : 'Create share link'}
          </button>
          <button onClick={download} disabled={dl} data-testid="vision-handoff-btn"
            className="inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-full bg-[#F97316] hover:bg-[#EA580C] disabled:opacity-60 transition-colors">
            {dl ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Download handoff package
          </button>
        </div>
      </div>
      {shareUrl && (
        <div className="mt-3 flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2">
          <a href={shareUrl} target="_blank" rel="noopener noreferrer" data-testid="vision-share-url" className="text-xs font-mono text-sky-300 truncate flex-1 hover:underline">{shareUrl}</a>
          <button type="button" onClick={() => { navigator.clipboard?.writeText(shareUrl); toast.success('Copied'); }} className="text-slate-300 hover:text-white"><Copy size={14} /></button>
          <a href={shareUrl} target="_blank" rel="noopener noreferrer" className="text-slate-300 hover:text-white"><ExternalLink size={14} /></a>
        </div>
      )}
      {ap && (
        <div className="mt-3 text-xs bg-white/5 border border-white/10 rounded-lg px-3 py-2" data-testid="vision-approval-status">
          <ApprovalBadge approval={ap} />
          <span className="ml-2 text-slate-300">{ap.name ? `by ${ap.name} · ` : ''}{new Date(ap.at).toLocaleString()}</span>
          {ap.comment && <div className="mt-1 text-slate-100 whitespace-pre-wrap">“{ap.comment}”</div>}
        </div>
      )}
    </div>
  );
}
